import React, { useState, useEffect, useMemo } from "react"
import { useFeatureController } from "./FeatureController"
import { useScrollNavigation } from "./scroll-navigation"
import FeatureSection from "./FeatureSection"
import FeaturesScroll from "./FeaturesScroll"
import SideMenuSwitcher from "./SideMenuSwitcher"
import WaveBG from "../WaveBG"
import useSitePlatform from "@components/Site/useSitePlatform"

const AquaMailFeaturesSection = ({ group: initialGroup }) => {
  // Initialize state hooks
  const [platform] = useSitePlatform()
  const [isMobile, setIsMobile] = useState(false)

  // Initialize the controller once at the parent level
  const controller = useFeatureController({ 
    initialGroup 
  })

  // Destructure necessary values for this component
  const {
    getData,
    selectNextGroup: onNext,
    selectPrevGroup: onPrev,
    selectedFeature,
  } = controller

  // Memoize features to prevent unnecessary re-renders
  const features = useMemo(() => getData(), [getData])

  // Initialize mobile state on client-side only
  useEffect(() => {
    if (typeof window === "undefined") return

    const handleResize = () => {
      setIsMobile(window.innerWidth < 1280)
    }

    handleResize() // Initialize immediately
    window.addEventListener("resize", handleResize)

    return () => window.removeEventListener("resize", handleResize)
  }, [])

  // Handle group navigation
  useScrollNavigation({ onNext, onPrev })

  // Define UI constants
  const waveProps = useMemo(
    () => ({
      frequency: {
        default: 0.5,
        md: 0.9,
        lg: 1.1,
      },
      amplitude: {
        default: 40,
        md: 70,
        lg: 95,
      },
      phase: {
        default: 0,
        md: 400,
        lg: 400,
      },
      sampleRate: {
        default: 16,
        md: 32,
        lg: 32,
      },
    }),
    []
  )

  const waveStyle = useMemo(
    () => ({
      width: "100%",
      height: "320px",
    }),
    []
  )

  // Early return if there are no features
  if (!features?.length) return null

  return (
    <div className="relative z-0 flex flex-col items-center w-full  pt-[60px] md:pt-[90px] xl:pt-0 xl:mt-[40px] mx-auto overflow-hidden">
      {!isMobile && <FeatureSection controller={controller} />}
      {isMobile && (
        <div className="mt-6">
          <FeaturesScroll
            controller={controller}
            showDescription
            cardDistance={200}
            scrollHeight={{
              default: 520,
              xs: 320,
              sm: 440,
              md: 480,
              lg: 500,
            }}
          />
        </div>
      )}
      <SideMenuSwitcher platform={platform} controller={controller} />
      <WaveBG
        waveProps={waveProps}
        className="hidden md:block -z-[1] text-primary_lt"
        waveStyle={waveStyle}
      />
    </div>
  )
}

const AldooComponent = {
  id: 'AquaMailFeaturesSection',
  component: AquaMailFeaturesSection,
  input: {},
  meta: {
    name: 'Aqua Mail Features Section',
    description: 'Interactive feature showcase with platform-specific content',
    category: 'Marketing',
  },
}

export { AldooComponent }
export default AquaMailFeaturesSection
